import { ResearchOrganizationFundingType, ResearchOrganizationType } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { CompanyLogoField } from '@components/pages/company/components/company-company-profile-modal-logo-field.component';
import { PreviousGovernmentWorkSection } from '@components/pages/company/components/company-company-profile-modal-previous-government-work-section.component';
import { FormLabel as FormLabelComponent } from '@components/shared/form-label.component';
import { Button } from '@components/ui/button';
import { withForm } from '@components/ui/form/form';
import { FormLabel } from '@components/ui/form/shared.component';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import { companyFormSchemaBase } from '@schemas/company.schema';
import { DEFAULT_COUNTRY, RESEARCH_ORGANIZATION_TYPE_LABELS, countries, usStates } from '@utils/constants';
import { resetFieldMeta } from '@utils/form.util';
import { formatProper } from '@utils/text.util';
import { XIcon } from 'lucide-react';
import { companyProfileModalDetailsAdditionalDefaultProps } from './company-company-profile-modal-details.types';
import { companyFormOptions } from './company-form-options';

const stateOptions = usStates.map((state) => ({
  label: `${state.name} (${state.code})`,
  value: state.code,
}));

const organizationTypeOptions = Object.values(ResearchOrganizationType).map((value) => ({
  label: RESEARCH_ORGANIZATION_TYPE_LABELS[value],
  value,
}));

const fundingTypeOptions = Object.values(ResearchOrganizationFundingType).map((value) => ({
  label: formatProper(value.toLowerCase().replace(/_/g, ' ')),
  value,
}));

export const DetailsResearchSection = withForm({
  ...companyFormOptions,
  props: companyProfileModalDetailsAdditionalDefaultProps,
  render: function Render(props) {
    const {
      companyLogoError,
      companyLogoPreviewUrl,
      form,
      isCompanyLogoDeleteRequested,
      onCompanyLogoFileSelect,
      onCompanyLogoRemove,
      onCompanyLogoRestore,
    } = props;

    return (
      <div className="flex w-full flex-col gap-6">
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-[10rem_minmax(0,1fr)] md:items-start">
          <CompanyLogoField
            companyLogoError={companyLogoError}
            companyLogoPreviewUrl={companyLogoPreviewUrl}
            isCompanyLogoDeleteRequested={isCompanyLogoDeleteRequested}
            onCompanyLogoFileSelect={onCompanyLogoFileSelect}
            onCompanyLogoRemove={onCompanyLogoRemove}
            onCompanyLogoRestore={onCompanyLogoRestore}
          />
          <div className="grid w-full grid-cols-1 gap-4 md:pt-7">
            <form.AppField
              name="name"
              validators={{
                onBlur: companyFormSchemaBase.shape.name,
              }}
            >
              {(field) => (
                <field.TextField
                  autoComplete="off"
                  data-1p-ignore
                  label="Organization Name"
                  placeholder={'e.g., "Duke University", "Monash University"'}
                  required
                  type="text"
                />
              )}
            </form.AppField>

            <form.AppField name="researchOrganizationData.type">
              {(field) => (
                <field.SelectField
                  clearable
                  label="Organization Type"
                  options={organizationTypeOptions}
                  placeholder="Select organization type"
                />
              )}
            </form.AppField>
          </div>
        </div>

        <form.AppField
          name="description"
          validators={{
            onBlur: companyFormSchemaBase.shape.description,
          }}
        >
          {(field) => (
            <field.TextAreaField
              label="Description"
              textareaClassName="flex h-28 w-full rounded-lg bg-gray-100 p-3"
            />
          )}
        </form.AppField>

        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
          <form.AppField
            listeners={{
              onChange: () => {
                const currentCountry = form.getFieldValue('country');
                if (currentCountry && currentCountry !== DEFAULT_COUNTRY) {
                  form.setFieldValue('state', '');
                }
              },
            }}
            name="country"
            validators={{
              onChange: companyFormSchemaBase.shape.country,
            }}
          >
            {(field) => (
              <field.SelectPopoverField
                className="w-full"
                emptyMessage="No country found"
                formatValue={(option) => option.label}
                label="Country"
                options={countries}
                placeholder="Select a country"
                searchPlaceholder="Search country..."
              />
            )}
          </form.AppField>

          <form.AppField
            name="state"
            validators={{
              onBlur: companyFormSchemaBase.shape.state,
            }}
          >
            {(field) => (
              <form.Subscribe selector={(state) => state.values.country}>
                {(country) =>
                  country === DEFAULT_COUNTRY ? (
                    <field.SelectField
                      className="w-full"
                      label="State"
                      options={stateOptions}
                      placeholder="Select a state"
                    />
                  ) : (
                    <field.TextField
                      label="State / Province"
                      placeholder="Enter state/province"
                      type="text"
                    />
                  )
                }
              </form.Subscribe>
            )}
          </form.AppField>

          <form.AppField
            name="city"
            validators={{
              onBlur: companyFormSchemaBase.shape.city,
            }}
          >
            {(field) => (
              <field.TextField
                autoComplete="off"
                data-1p-ignore
                label="City"
                type="text"
              />
            )}
          </form.AppField>
        </div>

        <form.AppField name="researchOrganizationData.departmentName">
          {(field) => (
            <field.TextField
              label="Department"
              placeholder="e.g. School name or other"
              type="text"
            />
          )}
        </form.AppField>

        <div>
          <p className="text-base font-medium text-gray-800">Department Head</p>
          <div className="mt-2 grid w-full grid-cols-1 gap-4 md:grid-cols-2">
            <form.AppField name="researchOrganizationData.departmentHeadContact.name">
              {(field) => (
                <field.TextField
                  label="Name"
                  placeholder="e.g. John Doe"
                  type="text"
                />
              )}
            </form.AppField>
            <form.AppField name="researchOrganizationData.departmentHeadContact.email">
              {(field) => (
                <field.TextField
                  label="Email"
                  placeholder="e.g. john.doe@example.com"
                  type="email"
                />
              )}
            </form.AppField>
          </div>
        </div>

        <div>
          <p className="text-base font-medium text-gray-800">Tech Transfer Contact</p>
          <div className="mt-2 grid w-full grid-cols-1 gap-4 md:grid-cols-2">
            <form.AppField name="researchOrganizationData.techTransferContact.name">
              {(field) => (
                <field.TextField
                  label="Name"
                  placeholder="e.g. John Doe"
                  type="text"
                />
              )}
            </form.AppField>
            <form.AppField name="researchOrganizationData.techTransferContact.email">
              {(field) => (
                <field.TextField
                  label="Email"
                  placeholder="e.g. john.doe@example.com"
                  type="email"
                />
              )}
            </form.AppField>
          </div>
        </div>

        <form.AppField name="researchOrganizationData.ipLicense">
          {(field) => {
            const selection = field.state.value === undefined ? undefined : field.state.value ? 'yes' : 'no';

            return (
              <div className="flex flex-col gap-2">
                <FormLabel>Intellectual Property License Established</FormLabel>
                <RadioGroup
                  className="flex flex-row gap-4"
                  value={selection ?? undefined}
                  onValueChange={(option) => {
                    field.handleChange(option === 'yes');
                  }}
                >
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <RadioGroupItem
                      id="ip-license-yes"
                      value="yes"
                    />
                    <label
                      className="cursor-pointer"
                      htmlFor="ip-license-yes"
                    >
                      Yes
                    </label>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <RadioGroupItem
                      id="ip-license-no"
                      value="no"
                    />
                    <label
                      className="cursor-pointer"
                      htmlFor="ip-license-no"
                    >
                      No
                    </label>
                  </div>
                </RadioGroup>
              </div>
            );
          }}
        </form.AppField>

        <form.AppField
          listeners={{
            onChange: ({ value }) => {
              if (!value) {
                form.setFieldMeta('researchOrganizationData.foreignAffiliates', resetFieldMeta);
                form.setFieldValue('researchOrganizationData.foreignAffiliates', undefined);
              }
            },
          }}
          name="hasForeignAffiliation"
        >
          {(foreignAffiliationField) => {
            const hasForeignAffiliation = Boolean(foreignAffiliationField.state.value);

            return (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <FormLabelComponent label="Foreign Affiliation" />
                  <RadioGroup
                    className="flex flex-row gap-4"
                    value={hasForeignAffiliation ? 'yes' : 'no'}
                    onValueChange={(value) => {
                      foreignAffiliationField.handleChange(value === 'yes');
                    }}
                  >
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <RadioGroupItem
                        id="foreign-affiliation-yes"
                        value="yes"
                      />
                      <label
                        className="cursor-pointer"
                        htmlFor="foreign-affiliation-yes"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <RadioGroupItem
                        id="foreign-affiliation-no"
                        value="no"
                      />
                      <label
                        className="cursor-pointer"
                        htmlFor="foreign-affiliation-no"
                      >
                        No
                      </label>
                    </div>
                  </RadioGroup>
                </div>

                {hasForeignAffiliation && (
                  <form.AppField
                    mode="array"
                    name="researchOrganizationData.foreignAffiliates"
                  >
                    {(affiliatesField) => {
                      const affiliates = affiliatesField.state.value;

                      return (
                        <div className="space-y-2">
                          {affiliates?.map((_, index) => (
                            <form.AppField
                              key={`foreign-affiliate-${index}`}
                              name={`researchOrganizationData.foreignAffiliates[${index}]`}
                            >
                              {(affiliateField) => (
                                <div className="flex items-start gap-2">
                                  <affiliateField.TextField
                                    className="flex-1"
                                    placeholder="Affiliate name"
                                    type="text"
                                  />
                                  <IconButton
                                    ariaLabel="Remove affiliate"
                                    icon={<XIcon size={16} />}
                                    size="lg"
                                    variant="secondary"
                                    onClick={() => affiliatesField.removeValue(index)}
                                  />
                                </div>
                              )}
                            </form.AppField>
                          ))}
                          <Button
                            variant="link"
                            onClick={() => affiliatesField.pushValue('')}
                          >
                            + Add Foreign Affiliate
                          </Button>
                        </div>
                      );
                    }}
                  </form.AppField>
                )}
              </div>
            );
          }}
        </form.AppField>

        <form.AppField
          listeners={{
            onChange: ({ value }) => {
              if (!value) {
                form.setFieldMeta('researchOrganizationData.funding.countries', resetFieldMeta);
                form.setFieldMeta('researchOrganizationData.funding.type', resetFieldMeta);
                form.setFieldValue('researchOrganizationData.funding', undefined);
              }
            },
          }}
          name="hasFunding"
        >
          {(fundingField) => {
            const hasFunding = Boolean(fundingField.state.value);

            return (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <FormLabelComponent label="Funding" />
                  <RadioGroup
                    className="flex flex-row gap-4"
                    value={hasFunding ? 'yes' : 'no'}
                    onValueChange={(value) => {
                      fundingField.handleChange(value === 'yes');
                    }}
                  >
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <RadioGroupItem
                        id="funding-yes"
                        value="yes"
                      />
                      <label
                        className="cursor-pointer"
                        htmlFor="funding-yes"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <RadioGroupItem
                        id="funding-no"
                        value="no"
                      />
                      <label
                        className="cursor-pointer"
                        htmlFor="funding-no"
                      >
                        No
                      </label>
                    </div>
                  </RadioGroup>
                </div>

                {hasFunding && (
                  <form.AppField
                    listeners={{
                      onChange: ({ value }) => {
                        if (value !== ResearchOrganizationFundingType.FOREIGN_NATION) {
                          form.setFieldMeta('researchOrganizationData.funding.countries', resetFieldMeta);
                          form.setFieldValue('researchOrganizationData.funding.countries', []);
                        }
                      },
                    }}
                    name="researchOrganizationData.funding.type"
                  >
                    {(fundingTypeField) => {
                      const isForeignFunding =
                        fundingTypeField.state.value === ResearchOrganizationFundingType.FOREIGN_NATION;

                      return (
                        <div className="flex flex-col gap-4">
                          <fundingTypeField.SelectField
                            clearable
                            label="Funding Type"
                            options={fundingTypeOptions}
                            placeholder="Select funding type"
                          />
                          {isForeignFunding && (
                            <form.AppField
                              mode="array"
                              name="researchOrganizationData.funding.countries"
                            >
                              {(countriesField) => (
                                <countriesField.SelectMultipleField
                                  className="w-full"
                                  label="Funding Countries"
                                  options={countries}
                                  placeholder="Select countries"
                                />
                              )}
                            </form.AppField>
                          )}
                        </div>
                      );
                    }}
                  </form.AppField>
                )}
              </div>
            );
          }}
        </form.AppField>

        <PreviousGovernmentWorkSection form={form} />
      </div>
    );
  },
});
